import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useExpense } from "@/contexts/ExpenseContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { Plus, DollarSign, CreditCard, TrendingDown, TrendingUp } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { colorOptions, getRandomColor } from "@/utils/colors";

export default function AddExpense() {
  const { categories, addTransaction, addCategory, loading } = useExpense();
  const { currency } = useCurrency();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    amount: '',
    category: '',
    categoryId: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    paymentType: 'cash',
    cardNumber: ''
  });
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    color: getRandomColor(),
    type: 'expense' as 'expense' | 'income'
  });

  // Get the selected category to determine if it's income or expense
  const selectedCategory = categories.find(cat => cat.id === formData.categoryId);
  const isIncomeCategory = selectedCategory?.type === 'income';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.amount || !formData.category || !formData.description) {
      toast.error(t('messages.fillAllFields'));
      return;
    }

    const amount = parseFloat(formData.amount);
    if (amount <= 0) {
      toast.error('Amount must be a positive number');
      return;
    }

    try {
      await addTransaction({
        amount: isIncomeCategory 
          ? Math.abs(parseFloat(formData.amount))  // Positive for income
          : -Math.abs(parseFloat(formData.amount)), // Negative for expense
        category: formData.category,
        categoryId: formData.categoryId,
        description: formData.description,
        date: formData.date,
        paymentType: formData.paymentType,
        cardNumber: formData.cardNumber,
        isPeriodicPayment: false
      });

      toast.success(isIncomeCategory ? t('messages.incomeAdded') : t('messages.expenseAdded'));
      navigate('/transactions');
    } catch (error) {
      console.error(`Failed to add ${isIncomeCategory ? 'income' : 'expense'}:`, error);
      toast.error(`Failed to add ${isIncomeCategory ? 'income' : 'expense'}`);
    }
  };

  const handleCategoryChange = (categoryName: string) => {
    const selectedCategory = categories.find(cat => cat.name === categoryName);
    setFormData(prev => ({
      ...prev,
      category: categoryName,
      categoryId: selectedCategory?.id || ''
    }));
  };

  const handleAmountKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const currentValue = parseFloat(formData.amount) || 0;
    const step = 0.01;
    
    // Block down arrow if it would make the value negative or zero
    if (e.key === 'ArrowDown' && currentValue - step <= 0) {
      e.preventDefault();
    }
  };

  const handleCategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!categoryForm.name.trim()) {
      toast.error(t('categories.categoryNameRequired'));
      return;
    }

    try {
      await addCategory(categoryForm);
      toast.success(t('categories.categoryAdded'));
      
      // Auto-select the new category in the main form
      setFormData(prev => ({
        ...prev,
        category: categoryForm.name,
        categoryId: '' // Will be set when categories refresh
      }));
      
      // Reset category form and close dialog
      setCategoryForm({ name: '', color: getRandomColor(), type: 'expense' });
      setCategoryDialogOpen(false);
    } catch (error) {
      toast.error(t('categories.categoryCreationFailed'));
    }
  };

  return (
    <div className="max-w-2xl mx-auto page-shell">
      <div className="page-heading">
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <Plus className="h-8 w-8 text-primary" />
          {isIncomeCategory ? 'Add New Income' : 'Add New Expense'}
        </h1>
        <p className="text-muted-foreground mt-1">
          {isIncomeCategory ? 'Record a new income transaction' : 'Record a new expense transaction'}
        </p>
      </div>

      <Card className="p-8 card-gradient border-border/90">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Input
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="What did you spend on?"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="amount">Amount ({currency.symbol})</Label>
            <div className="relative">
              <span className="absolute left-3 top-3 text-sm text-muted-foreground font-mono">{currency.symbol}</span>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0.01"
                value={formData.amount}
                onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                onKeyDown={handleAmountKeyDown}
                placeholder="0.00"
                className="pl-10"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="category">Category</Label>
              <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
                <DialogTrigger asChild>
                  <Button type="button" variant="outline" size="sm" className="gap-1">
                    <Plus className="h-3 w-3" />
                    {t('categories.new')}
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>{t('categories.createNewCategory')}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCategorySubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="category-name">{t('categories.categoryName')}</Label>
                      <Input
                        id="category-name"
                        value={categoryForm.name}
                        onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder={t('categories.categoryName')}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>{t('categories.categoryType')}</Label>
                      <Select value={categoryForm.type} onValueChange={(value: 'expense' | 'income') => setCategoryForm(prev => ({ ...prev, type: value }))}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="expense">
                            <div className="flex items-center gap-2">
                              <TrendingDown className="h-4 w-4 text-red-500" />
                              {t('categories.expense')}
                            </div>
                          </SelectItem>
                          <SelectItem value="income">
                            <div className="flex items-center gap-2">
                              <TrendingUp className="h-4 w-4 text-green-500" />
                              {t('categories.income')}
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>{t('categories.color')}</Label>
                      <div className="grid grid-cols-5 gap-1.5">
                        {colorOptions.map((color) => (
                          <button
                            key={color}
                            type="button"
                            className={`w-8 h-8 rounded-full border-2 ${
                              categoryForm.color === color ? 'border-gray-400' : 'border-gray-200'
                            }`}
                            style={{ backgroundColor: color }}
                            onClick={() => setCategoryForm(prev => ({ ...prev, color }))}
                          />
                        ))}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button type="submit" className="flex-1">
                        {t('categories.createCategory')}
                      </Button>
                      <Button type="button" variant="outline" onClick={() => setCategoryDialogOpen(false)}>
                        {t('categories.cancel')}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <Select value={formData.category} onValueChange={handleCategoryChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {/* Expense Categories */}
                <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">
                  EXPENSES
                </div>
                {categories
                  .filter(category => category.type === 'expense')
                  .map((category) => (
                    <SelectItem key={category.id} value={category.name}>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: category.color }}
                        />
                        {category.name}
                      </div>
                    </SelectItem>
                  ))}
                
                {/* Income Categories */}
                <div className="px-2 py-1 text-xs font-semibold text-muted-foreground mt-2">
                  INCOME
                </div>
                {categories
                  .filter(category => category.type === 'income')
                  .map((category) => (
                    <SelectItem key={category.id} value={category.name}>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: category.color }}
                        />
                        {category.name}
                      </div>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="paymentType">Payment Type</Label>
            <Select value={formData.paymentType} onValueChange={(value) => setFormData(prev => ({ ...prev, paymentType: value }))}>
              <SelectTrigger>
                <SelectValue placeholder="Select payment type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="credit_card">Credit Card</SelectItem>
                <SelectItem value="debit_card">Debit Card</SelectItem>
                <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                <SelectItem value="digital_wallet">Digital Wallet</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {formData.paymentType !== 'cash' && (
            <div className="space-y-2">
              <Label htmlFor="cardNumber">Card Number (Last 4 digits) - Optional</Label>
              <div className="relative">
                <CreditCard className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="cardNumber"
                  value={formData.cardNumber}
                  onChange={(e) => setFormData(prev => ({ ...prev, cardNumber: e.target.value }))}
                  placeholder="****1234 (optional)"
                  className="pl-10"
                  maxLength={8}
                  required={false}
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="date">Date</Label>
            <Input
              id="date"
              type="date"
              value={formData.date}
              onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
              required
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => navigate('/transactions')} className="flex-1" disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={loading}>
              {loading ? 'Adding...' : 'Add Expense'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}